<template>
  <main class="job-page">
    <section class="job-posting" v-reveal>
      <a class="job-back-button" href="/Karriere" :aria-label="career.backAction">
        <AppIcon name="chevron" />
      </a>

      <div class="job-posting__header">
        <span class="job-posting__icon">
          <AppIcon :name="job.icon" />
        </span>
        <p class="section-kicker">{{ career.detailIntroLabel }}</p>
        <h1>{{ job.title }}</h1>
        <p>{{ job.detailTitle }}</p>
        <p>{{ job.detailCopy }}</p>
        <p class="job-posting__notice">{{ career.detailNotice }}</p>
        <p class="job-posting__notice job-posting__notice--nda">
          <AppIcon name="lock" />
          <span>{{ career.ndaNotice.copy }}</span>
        </p>
      </div>

      <div class="job-posting__divider"></div>

      <div class="job-posting__profile">
        <p class="section-kicker">{{ career.detailRequirementsLabel }}</p>
        <h2>{{ career.detailRequirementsLabel }}</h2>
        <ul>
          <li v-for="requirement in job.requirements" :key="requirement">
            <AppIcon name="star" />
            <span>{{ requirement }}</span>
          </li>
        </ul>
        <p>{{ job.copy }}</p>
      </div>
    </section>

    <section id="application-portal" class="application-portal">
      <div class="application-portal__heading">
        <div>
          <p class="section-kicker">{{ career.portal.title }}</p>
          <h2>{{ career.portal.formTitle }}</h2>
        </div>
        <strong>{{ totalQuestionCount }} {{ totalQuestionCount === 1 ? career.portal.questionCountSingular : career.portal.questionCountPlural }}</strong>
      </div>

      <div v-if="authLoading" class="application-lock">
        <span>
          <AppIcon name="discord" />
        </span>
        <div>
          <h3>{{ career.portal.authLoadingTitle }}</h3>
          <p>{{ career.portal.authLoadingCopy }}</p>
        </div>
      </div>

      <div v-else-if="!isDiscordConnected" class="application-lock">
        <span>
          <AppIcon name="discord" />
        </span>
        <div>
          <h3>{{ career.portal.lockedTitle }}</h3>
          <p>{{ career.portal.lockedCopy }}</p>
          <p v-if="authError" class="application-lock__error">{{ authError }}</p>
        </div>
        <a class="action-button action-button--discord" :href="discordOAuthUrl">
          <AppIcon name="discord" />
          <span>{{ career.portal.connectAction }}</span>
        </a>
      </div>

      <form v-else class="application-form" @submit.prevent="submit">
        <div class="application-account">
          <img
            v-if="discordAccount.avatar"
            class="application-account__avatar"
            :src="discordAccount.avatar"
            :alt="discordAccount.name"
          />
          <span v-else>
            <AppIcon name="discord" />
          </span>
          <div>
            <small>{{ career.portal.connectedLabel }}</small>
            <strong>{{ connectedDiscordLabel }}</strong>
          </div>
          <button type="button" @click="disconnectDiscord">{{ career.portal.disconnectAction }}</button>
        </div>

        <section
          v-for="section in questionnaireSections"
          :key="section.key"
          class="application-questionnaire"
        >
          <header class="application-questionnaire__header">
            <div>
              <small>{{ section.questions.length }} {{ section.questions.length === 1 ? career.portal.questionCountSingular : career.portal.questionCountPlural }}</small>
              <h3>{{ section.title }}</h3>
              <p v-if="section.copy">{{ section.copy }}</p>
            </div>
            <span v-if="section.optional">{{ career.portal.optionalLabel }}</span>
          </header>
          <article
            v-for="(question, questionIndex) in section.questions"
            :key="getQuestionKey(section.key, questionIndex)"
            class="application-question"
            :class="`application-question--${getQuestionType(question)}`"
          >
            <span>
              <strong>{{ questionIndex + 1 }}</strong>
              {{ question }}
            </span>
            <div
              v-if="getQuestionType(question) === 'choice'"
              class="application-choice-grid"
            >
              <label
                v-for="option in getQuestionOptions(question)"
                :key="option"
                class="application-choice"
              >
                <input
                  v-model="form[getQuestionKey(section.key, questionIndex)]"
                  type="radio"
                  :name="getQuestionKey(section.key, questionIndex)"
                  :value="option"
                  :required="!section.optional"
                />
                <span>{{ option }}</span>
              </label>
            </div>
            <div
              v-else-if="getQuestionType(question) === 'multiChoice'"
              class="application-choice-text"
            >
              <div class="application-choice-grid">
                <label
                  v-for="option in getQuestionOptions(question)"
                  :key="option"
                  class="application-choice"
                >
                  <input
                    v-model="form[getQuestionKey(section.key, questionIndex)]"
                    type="checkbox"
                    :value="option"
                  />
                  <span>{{ option }}</span>
                </label>
              </div>
              <input
                v-model="details[getQuestionKey(section.key, questionIndex)]"
                :aria-label="`${question} Details`"
                :placeholder="getQuestionDetailPlaceholder(question)"
              />
            </div>
            <div
              v-else-if="getQuestionType(question) === 'choiceText'"
              class="application-choice-text"
            >
              <div class="application-choice-grid">
                <label
                  v-for="option in getQuestionOptions(question)"
                  :key="option"
                  class="application-choice"
                >
                  <input
                    v-model="form[getQuestionKey(section.key, questionIndex)]"
                    type="radio"
                    :name="getQuestionKey(section.key, questionIndex)"
                    :value="option"
                    :required="!section.optional"
                  />
                  <span>{{ option }}</span>
                </label>
              </div>
              <textarea
                v-model="details[getQuestionKey(section.key, questionIndex)]"
                :placeholder="getQuestionDetailPlaceholder(question)"
              ></textarea>
            </div>
            <div
              v-else-if="getQuestionType(question) === 'scale'"
              class="application-scale"
            >
              <label v-for="value in scaleOptions" :key="value">
                <input
                  v-model="form[getQuestionKey(section.key, questionIndex)]"
                  type="radio"
                  :name="getQuestionKey(section.key, questionIndex)"
                  :value="value"
                  :required="!section.optional"
                />
                <span>{{ value }}</span>
              </label>
            </div>
            <input
              v-else-if="getQuestionType(question) === 'short'"
              v-model="form[getQuestionKey(section.key, questionIndex)]"
              :aria-label="question"
              :placeholder="getQuestionPlaceholder(question)"
              :required="!section.optional"
            />
            <input
              v-else-if="getQuestionType(question) === 'url'"
              v-model="form[getQuestionKey(section.key, questionIndex)]"
              :aria-label="question"
              :placeholder="getQuestionPlaceholder(question)"
              :required="!section.optional"
              inputmode="url"
            />
            <textarea
              v-else
              v-model="form[getQuestionKey(section.key, questionIndex)]"
              :placeholder="getQuestionPlaceholder(question)"
              :required="!section.optional"
            ></textarea>
          </article>
        </section>

        <div class="application-form__footer">
          <p v-if="submitted">
            <strong>{{ career.portal.successTitle }}</strong>
            <span>{{ career.portal.successCopy }}</span>
          </p>
          <p v-else-if="submitError" class="application-form__error">
            <strong>{{ career.portal.submitErrorTitle }}</strong>
            <span>{{ submitError }}</span>
          </p>
          <button
            class="action-button action-button--discord action-button--large"
            type="submit"
            :disabled="submitLoading"
          >
            <AppIcon name="spark" />
            <span>{{ submitLoading ? career.portal.submitLoadingAction : career.portal.submitAction }}</span>
          </button>
        </div>
      </form>
    </section>

    <div class="job-tags" v-reveal>
      <strong v-for="tag in job.tags" :key="tag">{{ tag }}</strong>
    </div>
  </main>
</template>

<script>
import AppIcon from './AppIcon.vue'
import {
  disconnectDiscordAccount,
  exchangeDiscordOAuthCode,
  getConnectedDiscordAccount,
  getDiscordOAuthResult,
  getDiscordOAuthUrl,
  submitApplication
} from '../services/applicationPortalService'

export default {
  name: 'JobPage',
  components: {
    AppIcon
  },
  props: {
    career: {
      type: Object,
      required: true
    },
    job: {
      type: Object,
      required: true
    }
  },
  data() {
    const storedDiscordAccount = getConnectedDiscordAccount()

    return {
      discordAccount: storedDiscordAccount?.authSource === 'api' ? storedDiscordAccount : null,
      discordOAuthResult: getDiscordOAuthResult(),
      authLoading: false,
      authError: '',
      submitted: false,
      submitLoading: false,
      submitError: '',
      details: {},
      form: {},
      scaleOptions: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    }
  },
  created() {
    this.form = this.getEmptyForm()
  },
  mounted() {
    this.authenticateDiscordCode()
  },
  watch: {
    job() {
      this.submitted = false
      this.submitError = ''
      this.form = this.getEmptyForm()
      this.details = {}
    },
    career() {
      this.submitError = ''
      this.form = this.getEmptyForm()
      this.details = {}
    }
  },
  computed: {
    questionnaireSections() {
      return [
        {
          key: 'general',
          title: this.career.portal.generalQuestionsTitle,
          copy: this.career.portal.generalQuestionsCopy,
          questions: this.career.portal.generalQuestions || []
        },
        {
          key: 'role',
          title: this.job.questionnaireTitle || this.job.title,
          questions: this.job.questions || []
        },
        {
          key: 'optional',
          title: this.career.portal.optionalQuestionsTitle,
          copy: this.career.portal.optionalQuestionsCopy,
          questions: this.career.portal.optionalQuestions || [],
          optional: true
        }
      ].filter((section) => section.questions.length)
    },
    totalQuestionCount() {
      return this.questionnaireSections.reduce((total, section) => total + section.questions.length, 0)
    },
    discordOAuthUrl() {
      return getDiscordOAuthUrl()
    },
    isDiscordConnected() {
      return Boolean(this.discordAccount)
    },
    connectedDiscordLabel() {
      if (this.discordAccount?.name) {
        return this.discordAccount.name
      }

      if (this.discordAccount?.user?.username) {
        return `@${this.discordAccount.user.username}`
      }

      if (this.discordAccount?.username) {
        return `@${this.discordAccount.username}`
      }

      if (this.discordAccount?.handle) {
        return this.discordAccount.handle
      }

      return 'Discord OAuth verbunden'
    },
    isEnglishContent() {
      return this.career.portal.yesOption === 'Yes'
    }
  },
  methods: {
    async authenticateDiscordCode() {
      if (!this.discordOAuthResult?.code || this.discordAccount) {
        return
      }

      this.authLoading = true
      this.authError = ''

      try {
        this.discordAccount = await exchangeDiscordOAuthCode(this.discordOAuthResult.code)
        this.discordOAuthResult = null
        window.history.replaceState({}, document.title, window.location.pathname)
      } catch (error) {
        this.authError = error.message || this.career.portal.authError
        this.discordAccount = null
      } finally {
        this.authLoading = false
      }
    },
    getEmptyForm() {
      return this.questionnaireSections.reduce((form, section) => {
        section.questions.forEach((question, questionIndex) => {
          form[this.getQuestionKey(section.key, questionIndex)] =
            this.getQuestionType(question) === 'multiChoice' ? [] : ''
        })

        return form
      }, {})
    },
    getQuestionKey(sectionKey, questionIndex) {
      return `${sectionKey}_${questionIndex}`
    },
    getQuestionType(question) {
      const normalizedQuestion = question.toLowerCase()

      if (
        normalizedQuestion.includes('1-10') ||
        normalizedQuestion.includes('1–10') ||
        normalizedQuestion.includes('wie wichtig') ||
        normalizedQuestion.includes('how important')
      ) {
        return 'scale'
      }

      if (
        normalizedQuestion.includes('wie hast du von uns erfahren') ||
        normalizedQuestion.includes('how did you hear about us')
      ) {
        return 'choice'
      }

      if (
        normalizedQuestion.includes('wie viel zeit') ||
        normalizedQuestion.includes('wie aktiv') ||
        normalizedQuestion.includes('seit wann') ||
        normalizedQuestion.includes('zeitzone') ||
        normalizedQuestion.includes('land lebst') ||
        normalizedQuestion.includes('discord username') ||
        normalizedQuestion.includes('wie heißt') ||
        normalizedQuestion.includes('wie alt') ||
        normalizedQuestion.includes('how much time') ||
        normalizedQuestion.includes('how active') ||
        normalizedQuestion.includes('since when') ||
        normalizedQuestion.includes('time zone') ||
        normalizedQuestion.includes('country') ||
        normalizedQuestion.includes('what is your name') ||
        normalizedQuestion.includes('how old') ||
        normalizedQuestion.includes('discord username')
      ) {
        return 'short'
      }

      if (
        normalizedQuestion.includes('welche rolle') ||
        normalizedQuestion.includes('which role')
      ) {
        return 'choice'
      }

      if (
        normalizedQuestion.includes('welche programmiersprachen') ||
        normalizedQuestion.includes('welche backend-sprachen') ||
        normalizedQuestion.includes('welche frontend-technologien') ||
        normalizedQuestion.includes('welche plattformen') ||
        normalizedQuestion.includes('discord libraries') ||
        normalizedQuestion.includes('which programming languages') ||
        normalizedQuestion.includes('which backend languages') ||
        normalizedQuestion.includes('which frontend technologies') ||
        normalizedQuestion.includes('which platforms')
      ) {
        return 'multiChoice'
      }

      if (
        normalizedQuestion.includes('wenn ja') ||
        normalizedQuestion.includes('hast du erfahrung') ||
        normalizedQuestion.includes('hast du bereits erfahrung') ||
        normalizedQuestion.includes('hast du bereits teams') ||
        normalizedQuestion.includes('hast du schon') ||
        normalizedQuestion.includes('kennst du dich mit') ||
        normalizedQuestion.includes('do you have experience') ||
        normalizedQuestion.includes('have you already') ||
        normalizedQuestion.includes('have you worked') ||
        normalizedQuestion.includes('do you know')
      ) {
        return 'choiceText'
      }

      if (
        normalizedQuestion.includes('besitzt du') ||
        normalizedQuestion.includes('waerst du') ||
        normalizedQuestion.includes('wärst du') ||
        normalizedQuestion.includes('kannst du ruhig') ||
        normalizedQuestion.includes('kannst du unter zeitdruck') ||
        normalizedQuestion.includes('kannst du konflikte') ||
        normalizedQuestion.includes('can you stay calm') ||
        normalizedQuestion.includes('can you work under time pressure') ||
        normalizedQuestion.includes('can you resolve conflicts') ||
        normalizedQuestion.includes('would you be willing') ||
        normalizedQuestion.includes('working microphone')
      ) {
        return 'choice'
      }

      if (
        normalizedQuestion.includes('arbeitest du lieber') ||
        normalizedQuestion.includes('vue, react') ||
        normalizedQuestion.includes('alleine oder im team') ||
        normalizedQuestion.includes('designs selbst erstellen') ||
        normalizedQuestion.includes('working alone or in a team') ||
        normalizedQuestion.includes('prefer vue, react') ||
        normalizedQuestion.includes('create designs yourself')
      ) {
        return 'choice'
      }

      if (
        normalizedQuestion.includes('projekt') ||
        normalizedQuestion.includes('repository') ||
        normalizedQuestion.includes('github') ||
        normalizedQuestion.includes('designs') ||
        normalizedQuestion.includes('beispiele') ||
        normalizedQuestion.includes('work samples') ||
        normalizedQuestion.includes('examples')
      ) {
        return 'url'
      }

      if (
        normalizedQuestion.includes('kannst du grafiken') ||
        normalizedQuestion.includes('can you create graphics')
      ) {
        return 'multiChoice'
      }

      return 'long'
    },
    getQuestionOptions(question) {
      const normalizedQuestion = question.toLowerCase()

      if (normalizedQuestion.includes('arbeitest du lieber') || normalizedQuestion.includes('alleine oder im team')) {
        return ['Im Team', 'Alleine', 'Beides']
      }

      if (normalizedQuestion.includes('working alone or in a team')) {
        return ['Team', 'Alone', 'Both']
      }

      if (normalizedQuestion.includes('wie hast du von uns erfahren')) {
        return ['Discord', 'TikTok', 'Freunde', 'Website', 'Sonstiges']
      }

      if (normalizedQuestion.includes('how did you hear about us')) {
        return ['Discord', 'TikTok', 'Friends', 'Website', 'Other']
      }

      if (normalizedQuestion.includes('vue, react')) {
        return ['Vue', 'React', 'Etwas anderes']
      }

      if (normalizedQuestion.includes('prefer vue, react')) {
        return ['Vue', 'React', 'Something else']
      }

      if (normalizedQuestion.includes('designs selbst erstellen')) {
        return ['Selbst erstellen', 'Nur umsetzen', 'Beides']
      }

      if (normalizedQuestion.includes('create designs yourself')) {
        return ['Create myself', 'Only implement', 'Both']
      }

      if (normalizedQuestion.includes('welche rolle')) {
        return [
          'Bot Developer',
          'Supporter',
          'Api Developer',
          'Community Manager',
          'Team Manager',
          'Marketing',
          normalizedQuestion.includes('zusätzlich') ? 'Keine zusätzliche' : 'Noch unsicher'
        ]
      }

      if (normalizedQuestion.includes('which role')) {
        return [
          'Bot Developer',
          'Supporter',
          'Api Developer',
          'Community Manager',
          'Team Manager',
          'Marketing',
          normalizedQuestion.includes('additional') ? 'No additional role' : 'Not sure yet'
        ]
      }

      if (normalizedQuestion.includes('programmiersprachen') || normalizedQuestion.includes('programming languages')) {
        return ['JavaScript']
      }

      if (normalizedQuestion.includes('discord libraries')) {
        return this.isEnglishContent
          ? ['discord.js', 'discord.py', 'JDA', 'Eris', 'Other', 'None yet']
          : ['discord.js', 'discord.py', 'JDA', 'Eris', 'Andere', 'Noch keine']
      }

      if (normalizedQuestion.includes('backend-sprachen') || normalizedQuestion.includes('backend languages')) {
        return ['JavaScript']
      }

      if (normalizedQuestion.includes('frontend-technologien') || normalizedQuestion.includes('frontend technologies')) {
        return ['HTML/CSS', 'JavaScript', 'TypeScript', 'Vue', 'React', 'Tailwind', 'Andere']
      }

      if (normalizedQuestion.includes('plattformen') || normalizedQuestion.includes('platforms')) {
        return ['TikTok', 'Discord', 'YouTube', 'Instagram', 'Website', 'Andere']
      }

      if (normalizedQuestion.includes('grafiken') || normalizedQuestion.includes('graphics')) {
        return this.isEnglishContent ? ['Graphics', 'Videos', 'Both', 'No'] : ['Grafiken', 'Videos', 'Beides', 'Nein']
      }

      if (this.getQuestionType(question) === 'choiceText') {
        return [this.career.portal.yesOption, this.career.portal.noOption]
      }

      return [this.career.portal.yesOption, this.career.portal.noOption]
    },
    getQuestionDetailPlaceholder(question) {
      const normalizedQuestion = question.toLowerCase()

      if (normalizedQuestion.includes('datenbanken')) {
        return this.career.portal.detailDatabasePlaceholder
      }

      if (
        normalizedQuestion.includes('programmiersprachen') ||
        normalizedQuestion.includes('backend-sprachen') ||
        normalizedQuestion.includes('frontend-technologien') ||
        normalizedQuestion.includes('plattformen') ||
        normalizedQuestion.includes('discord libraries') ||
        normalizedQuestion.includes('programming languages') ||
        normalizedQuestion.includes('backend languages') ||
        normalizedQuestion.includes('frontend technologies') ||
        normalizedQuestion.includes('platforms')
      ) {
        return this.career.portal.detailMoreInfoPlaceholder
      }

      if (normalizedQuestion.includes('frameworks')) {
        return this.career.portal.detailFrameworkPlaceholder
      }

      if (normalizedQuestion.includes('erfahrung') || normalizedQuestion.includes('experience')) {
        return this.career.portal.detailExperiencePlaceholder
      }

      return this.career.portal.detailPlaceholder
    },
    getQuestionPlaceholder(question) {
      const type = this.getQuestionType(question)

      if (type === 'short') {
        return this.career.portal.shortAnswerPlaceholder
      }

      if (type === 'url') {
        return this.career.portal.urlPlaceholder
      }

      return this.career.portal.answerPlaceholder
    },
    disconnectDiscord() {
      disconnectDiscordAccount()
      this.discordAccount = null
      this.discordOAuthResult = null
      this.submitted = false
      this.submitError = ''

      if (window.location.search.includes('code=')) {
        window.history.replaceState({}, document.title, window.location.pathname)
      }
    },
    async submit() {
      if (!this.discordAccount?.access) {
        this.submitError = this.career.portal.sessionInvalidError
        return
      }

      this.submitLoading = true
      this.submitError = ''

      try {
        await submitApplication({
          job: this.job.slug,
          jobTitle: this.job.title,
          applicant: {
            name: this.discordAccount.name,
            avatar: this.discordAccount.avatar
          },
          answers: this.questionnaireSections.map((section) => ({
            key: section.key,
            title: section.title,
            optional: Boolean(section.optional),
            questions: section.questions.map((question, questionIndex) => ({
              question,
              answer: this.form[this.getQuestionKey(section.key, questionIndex)] || '',
              details: this.details[this.getQuestionKey(section.key, questionIndex)] || ''
            }))
          }))
        }, this.discordAccount.access)

        this.submitted = true
      } catch (error) {
        this.submitError = error.message || this.career.portal.submitFallbackError
      } finally {
        this.submitLoading = false
      }
    }
  }
}
</script>
